interface ChatThreadRunRef {
  runId?: string | null;
}

interface ChatThreadMessageArtifactRef extends ChatThreadRunRef {
  id: string;
  role: 'user' | 'assistant';
  artifacts?: readonly unknown[] | null;
}

export interface ResolvePreferredThreadRunIdOptions {
  currentRunId?: string | null;
  latestRunId?: string | null;
  threadRunIds: readonly string[];
}

const normalizeRunId = (runId?: string | null): string | null => {
  if (typeof runId !== 'string') {
    return null;
  }
  const normalized = runId.trim();
  return normalized.length > 0 ? normalized : null;
};

export const collectThreadRunIds = (events: readonly ChatThreadRunRef[]): string[] => {
  const seen = new Set<string>();
  const runIds: string[] = [];

  events.forEach((event) => {
    const runId = normalizeRunId(event.runId);
    if (!runId || seen.has(runId)) {
      return;
    }
    seen.add(runId);
    runIds.push(runId);
  });

  return runIds;
};

export const resolvePreferredThreadRunId = ({
  currentRunId,
  latestRunId,
  threadRunIds
}: ResolvePreferredThreadRunIdOptions): string | null => {
  const normalizedLatestRunId = normalizeRunId(latestRunId);
  const normalizedCurrentRunId = normalizeRunId(currentRunId);
  const knownRunIds = new Set(threadRunIds.map((runId) => normalizeRunId(runId)).filter((runId): runId is string => Boolean(runId)));

  if (normalizedLatestRunId) {
    knownRunIds.add(normalizedLatestRunId);
  }
  if (normalizedCurrentRunId && knownRunIds.has(normalizedCurrentRunId)) {
    return normalizedCurrentRunId;
  }
  if (normalizedLatestRunId) {
    return normalizedLatestRunId;
  }
  return threadRunIds.length > 0 ? threadRunIds[threadRunIds.length - 1] ?? null : null;
};

export const hasFetchedRunArtifacts = (
  artifactsByRun: Record<string, unknown>,
  runId?: string | null
): boolean => {
  const normalizedRunId = normalizeRunId(runId);
  return normalizedRunId ? Object.prototype.hasOwnProperty.call(artifactsByRun, normalizedRunId) : false;
};

export const buildFallbackArtifactRunByMessageId = (
  events: readonly ChatThreadMessageArtifactRef[]
): Record<string, string> => {
  const lastAssistantMessageIdByRun = new Map<string, string>();
  const runsWithExplicitArtifacts = new Set<string>();

  events.forEach((event) => {
    if (event.role !== 'assistant') {
      return;
    }
    const runId = normalizeRunId(event.runId);
    if (!runId) {
      return;
    }
    lastAssistantMessageIdByRun.set(runId, event.id);
    if ((event.artifacts?.length ?? 0) > 0) {
      runsWithExplicitArtifacts.add(runId);
    }
  });

  const fallbackByMessageId: Record<string, string> = {};
  lastAssistantMessageIdByRun.forEach((messageId, runId) => {
    if (!runsWithExplicitArtifacts.has(runId)) {
      fallbackByMessageId[messageId] = runId;
    }
  });

  return fallbackByMessageId;
};
